import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Divider from '@mui/material/Divider';
import EqualizerIcon from '@mui/icons-material/InsertChartOutlined';

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import millerStability from '../lib/miller'
import toFixed from '../lib/toFixed'

function TwistRateCalculator() {
  const [ twistRate, setTwistRate ] = useAutoSaveState('twistRate', '7.5')
  const [ diameter, setDiameter ] = useAutoSaveState('diameter', '.243')
  const [ length, setLength ] = useAutoSaveState('length', '1.26')
  const [ grains, setGrains ] = useAutoSaveState('grains', '105')
  const [ velocity, setVelocity ] = useAutoSaveState('velocity', '3000')
  const [ temperature, setTemperature ] = useAutoSaveState('temperature', '72')
  const [ altitude, setAltitude ] = useAutoSaveState('altitude', '0')
  const [ preview, setPreview ] = useAutoSaveState('preview', null)
  const [ sg, setSg ] = useState('')

  const calculate = () => {
    const stability = millerStability(
      parseFloat(twistRate),
      parseFloat(diameter),
      parseFloat(length),
      parseFloat(grains),
      parseFloat(velocity),
      parseFloat(temperature),
      parseFloat(altitude)
    )
    setSg(stability)
    setPreview(`SG: ${toFixed(stability, 2)}`)
    window.gtag('event', 'calculate-twist-rate')
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3} justifyContent="center" >
        <Grid container justifyContent="center" xs={12} style={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            Twist Rate Calculator
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Twist Rate" autoFocus value={twistRate} onChange={e => setTwistRate(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">in/rev</InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Bullet Diameter" value={diameter} onChange={e => setDiameter(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">inches</InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Bullet Length" value={length} onChange={e => setLength(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">inches</InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Bullet Weight" value={grains} onChange={e => setGrains(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">grains</InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Velocity" value={velocity} onChange={e => setVelocity(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">fps</InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Temperature" value={temperature} onChange={e => setTemperature(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">°F</InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Altitude" value={altitude} onChange={e => setAltitude(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">feet</InputAdornment> }} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Button variant="contained" onClick={calculate}>Calculate</Button>
            </Grid>
          </Paper>
        </Grid>
        {sg && <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Typography variant="h5" gutterBottom>
                <EqualizerIcon /> Miller Stability Results
              </Typography>
            </Grid>
            <Divider style={{ marginBottom: '20px', marginTop: '20px' }} />
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="h4"><b>SG:</b> {toFixed(sg, 2)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-around" mb={2}>
              {sg < 1 && <Typography variant="h6" sx={{ color: 'red', textAlign: 'center', fontWeight: 'bold' }} >
                Bullet is Unstable
              </Typography>}
              {sg >= 1 && sg < 1.5 && <Typography variant="h6" sx={{ color: 'orange', textAlign: 'center', fontWeight: 'bold' }} >
                Bullet is Marginally Stable
              </Typography>}
              {sg >= 1.5 && <Typography variant="h6" sx={{ color: 'green', textAlign: 'center', fontWeight: 'bold' }} >
                Bullet is Stable
              </Typography>}
            </Box>
          </Paper>
        </Grid>}
      </Grid>
    </Container>
  );
}

export default function WrappedTwistRateCalculator() {
  return (
    <AutoSaveProvider>
      <TwistRateCalculator />
    </AutoSaveProvider>
  )
}
